import {
  Hub,
  HubStatus,
  HubSection,
  HubType,
} from '@alucio/aws-beacon-amplify/src/models';
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  commonReducers,
  hasMetadata,
  initialState,
  SliceState,
  duplicateModelRecordStandard,
} from './common';
import { DataStore } from 'aws-amplify/datastore';
import ActiveUser from '../../global/ActiveUser'
import * as logger from 'src/utils/logger';

const sliceName = 'hub'

const { reducers, extraReducers } = commonReducers<Hub>(sliceName)

interface CreateHubPayload {
  name: string
  status: HubStatus
  hubSections: HubSection[]
  meetingId?: string,
  callback: (hub: Hub) => void,
  templateId?: string,
  hubType: HubType,
}

interface EditHubPayload {
  hub: Hub,
  updatedContent: Hub
  callback?: () => void
}

const hubSlice = createSlice({
  name: sliceName,
  initialState: initialState<Hub>(),
  reducers: {
    ...reducers,
    createHub: (
      _state: SliceState<Hub>,
      action: PayloadAction<CreateHubPayload>,
    ): void => {
      const { payload } = action;
      const user = ActiveUser.user
      const now = new Date().toISOString()
      const hub = new Hub({
        tenantId: user!.tenantId,
        name: payload.name,
        status: payload.status,
        createdAt: now,
        createdBy: user!.id,
        updatedAt: now,
        updatedBy: user!.id,
        hubSections: payload.hubSections || [],
        meetingId: payload.meetingId,
        hubType: payload.hubType,
        templateId: payload.templateId,
      });

      DataStore.save(hub).then((hub) => {
        payload.callback?.(hub)
        analytics?.track('HUB_CREATE', {
          action: 'CREATE',
          category: 'HUB',
          hubId: hub.id,
          sourceHubId: hub.templateId,
        });
      }).catch((e) => {
        const errorText = 'Failed to create new hub'
        logger.hub.hubManagement.error(errorText, e)
        throw Error(errorText, e)
      })
    },
    // may not be needed since this is very similar to createHub
    createHubTemplate: (
      _state: SliceState<Hub>,
      action: PayloadAction<CreateHubPayload>,
    ): void => {
      const { payload } = action;
      const user = ActiveUser.user
      const now = new Date().toISOString()
      const hub = new Hub({
        tenantId: user!.tenantId,
        name: payload.name,
        status: payload.status,
        createdAt: now,
        createdBy: user!.id,
        updatedAt: now,
        updatedBy: user!.id,
        editPermissions: [`TenantPublisher-${user!.tenantId}`],
        hubSections: payload.hubSections || [],
        meetingId: payload.meetingId,
        hubType: HubType.TEMPLATE,
        lockName: true,
      });

      DataStore.save(hub).then((hub) => {
        payload.callback?.(hub)

        analytics?.track('HUB_TEMPLATE_CREATE', {
          action: 'CREATE',
          category: 'HUB',
        });
      }).catch((e) => {
        const errorText = 'Failed to create new hub template'
        logger.hub.hubManagement.error(errorText, e)
        throw Error(errorText, e)
      })
    },
    updateHub: (_state: SliceState<Hub>, action: PayloadAction<EditHubPayload>): void => {
      const { hub, updatedContent, callback } = action.payload
      const user = ActiveUser.user
      const now = new Date().toISOString()

      const updatedHub = Hub.copyOf(hub, (updated) => {
        updated.name = updatedContent.name
        updated.updatedAt = now
        updated.updatedBy = user!.id
        updated.hubSections = updatedContent.hubSections || []
        updated.sharedPermissions = updatedContent.sharedPermissions || []
        updated.readPermissions = updatedContent.readPermissions || []
        updated.lockName = updatedContent.lockName || false
      })

      DataStore.save(updatedHub).then(() => {
        callback?.()
      }).catch((e) => {
        const errorText = 'Failed to update hub'
        logger.hub.hubManagement.error(errorText, e)
        throw Error(errorText, e)
      })
    },
    duplicateHub: (_state: SliceState<Hub>, action: PayloadAction<{hub: Hub,
      callback: (status: string) => void}>): void => {
      const { hub } = action.payload
      const user = ActiveUser.user
      const now = new Date().toISOString()
      if (!user) {
        const errorText = 'Active user is not set when trying to duplicate hub';
        logger.hub.hubManagement.error(errorText)
        throw new Error(errorText)
      }

      const duplicatedSections = duplicateModelRecordStandard(hub.hubSections || [], {
        blacklist: ['_version', '_lastChangedAt', '_deleted'],
        timeFields: ['createdAt', 'updatedAt'],
      })
      const isMetadataRecord = hasMetadata(hub)
      if (isMetadataRecord) {
        const { _version, _lastChangedAt, _deleted, ...cleanHub } = hub
        const duplicateHub = new Hub({
          ...cleanHub,
          name: `${hub.name} Copy`,
          createdAt: now,
          createdBy: String(user.id),
          updatedAt: now,
          updatedBy: String(user.id),
          copiedFromHubId: hub.templateId || hub.id,
          hubSections: duplicatedSections,
          sharedPermissions: [],
        })

        DataStore.save(duplicateHub).then(() => {
          action.payload.callback('success')
          logger.hub.hubManagement.debug(`Hub duplicated successfully, 
          source hub id: ${hub.id} and duplicate hub id: ${duplicateHub.id}`)
          analytics?.track('HUB_CREATE', {
            action: 'CREATE',
            category: 'HUB',
            hubId: duplicateHub.id,
            sourceHubId: hub.templateId,
            copiedFromHubId: hub.id,
          });
        }).catch((e) => {
          const errorText = 'Failed to duplicate hub'
          logger.hub.hubManagement.error(errorText, e)
          action.payload.callback('error')
        })
      } else {
        const errorText = 'Hub does not have metadata'
        logger.hub.hubManagement.error(errorText)
        action.payload.callback('error')
      }
    },
    deleteHub: {
      prepare: (hub: Hub) => {
        analytics?.track('HUB_DELETE', {
          action: 'DELETE',
          category: 'HUB',
          hubId: hub.id,
        });
        return {
          payload: {
            model: Hub,
            entity: hub,
            updates: {
              updatedAt: (new Date().toISOString()),
              updatedBy: ActiveUser?.user?.id,
              status: HubStatus.DELETED,
            },
          },
        }
      },
      reducer: reducers.save,
    },
    updateSharePermission: {
      prepare: (hub: Hub) => {
        const sharePermissions = hub.sharedPermissions?.map((permission) => {
          return {
            ...permission,
            isDeleted: true,
            updatedAt: (new Date().toISOString()),
            updatedBy: ActiveUser?.user?.id || '',
            deletedAt: (new Date().toISOString()),
            deletedBy: ActiveUser?.user?.id || '',
          }
        })

        return {
          payload: {
            model: Hub,
            entity: hub,
            updates: {
              updatedBy: ActiveUser?.user?.id,
              updatedAt: (new Date().toISOString()),
              sharedPermissions: sharePermissions || [],
            },
          },
        }
      },
      reducer: reducers.save,
    },
  },
  extraReducers,
});

export default hubSlice;
export const hubActions = {
  ...hubSlice.actions,
};
